import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Link } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useThemeLabels } from '../../hooks/useThemeLabels';

/**
 * FieldHelperLink - Link para editar um dado derivado de entidade
 *
 * TODO 4.1: Componente que mostra um link para a página de edição da entidade
 * quando o campo exibido é derivado de cadastros (profissional, local, serviço, agenda).
 *
 * Props:
 *   - entity: 'professional' | 'place' | 'service' | 'schedule' (obrigatório)
 *   - id?: string (opcional) - ID da entidade para link direto
 *
 * Renderiza:
 *   Caption: "Este dado vem do cadastro em **<Entidade>**. Para alterar, edite em [link]."
 *
 * Obrigações:
 *   - O5: usar getLabel() para labels pt-BR
 *   - O10: em src/components/pages/
 *   - O12: padrão para campos derivados de entidade
 */

const FieldHelperLink = ({ entity = 'professional', id = null }) => {
  const { getLabel } = useThemeLabels();

  // Map entity para rota e label pt-BR
  const entityMap = {
    professional: {
      route: id ? `/professionals/${id}` : '/professionals',
      label: getLabel('professionals') || 'Profissionais',
    },
    place: {
      route: id ? `/places/${id}` : '/places',
      label: getLabel('places') || 'Locais',
    },
    service: {
      route: id ? `/services/${id}` : '/services',
      label: getLabel('services') || 'Serviços',
    },
    schedule: {
      route: id ? `/schedules/${id}` : '/schedules',
      label: getLabel('schedules') || 'Agendas',
    },
  };

  const entityInfo = entityMap[entity];

  if (!entityInfo) {
    console.warn(`[FieldHelperLink] Entity not supported: ${entity}`);
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.5,
      }}
      role="region"
      aria-label={`Link para editar ${entityInfo.label.toLowerCase()}`}
    >
      <Typography variant="caption" color="text.secondary">
        Este dado vem do cadastro em{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          {entityInfo.label}
        </Typography>
        . Para alterar, edite em{' '}
        <Link
          href={entityInfo.route}
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            fontWeight: 600,
            cursor: 'pointer',
            color: 'primary.main',
          }}
          aria-label={`Editar ${entityInfo.label.toLowerCase()}`}
        >
          {entityInfo.label}
          <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
        </Link>
        .
      </Typography>
    </Box>
  );
};

FieldHelperLink.propTypes = {
  entity: PropTypes.oneOf(['professional', 'place', 'service', 'schedule']).isRequired,
  id: PropTypes.string,
};

export default FieldHelperLink;
